import React from 'react';
import { Helmet } from 'react-helmet-async';

const Refunds = () => {
  return (
    <>
      <Helmet>
        <title>Refund Policy - Dental Stores</title>
        <meta name="description" content="Learn about Dental Stores refund policy, eligibility criteria, and how to initiate a refund." />
      </Helmet>
      <div style={{ padding: '48px 24px 96px', maxWidth: '960px', margin: '0 auto', color: '#1f2933' }}>
        <header style={{ marginBottom: '48px' }}>
          <p style={{ margin: 0, color: '#1565C0', fontWeight: '600', letterSpacing: '0.35em', textTransform: 'uppercase', fontSize: '12px' }}>
            Returns & refunds
          </p>
          <h1 style={{ fontSize: '38px', fontWeight: '700', margin: '14px 0 0', lineHeight: 1.25, color: '#102a43' }}>
            Refund Policy
          </h1>
          <p style={{ marginTop: '18px', fontSize: '17px', lineHeight: 1.7, maxWidth: '720px', color: '#52606d' }}>
            We want every order to feel right. Here's how we help when something needs to be returned.
          </p>
        </header>

        <section style={{ display: 'grid', gap: '40px' }}>
          <article style={{ display: 'grid', gap: '12px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>Overview</h2>
            <p style={{ margin: 0, fontSize: '17px', lineHeight: 1.7, color: '#52606d' }}>
              At Dental Stores, we strive to ensure your complete satisfaction with every purchase. If you're not happy with your order, our refund policy is designed to make returns straightforward and hassle-free.
            </p>
          </article>

          <article style={{ display: 'grid', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>Refund Eligibility</h2>
              <p style={{ margin: '8px 0 0', fontSize: '16px', color: '#52606d' }}>You may be eligible for a refund if:</p>
            </div>
            <ul style={{ margin: 0, paddingLeft: '22px', display: 'grid', gap: '10px', fontSize: '16px', color: '#1f2933' }}>
              <li>The product is unused and in its original packaging.</li>
              <li>The product was received within the last 30 days.</li>
              <li>The product is not a custom-made or personalized item.</li>
              <li>You have a valid proof of purchase.</li>
              <li>The return is due to our error (wrong item, damaged product, etc.).</li>
            </ul>
          </article>

          <article style={{ display: 'grid', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>Non-Refundable Items</h2>
              <p style={{ margin: '8px 0 0', fontSize: '16px', color: '#52606d' }}>These items are not eligible for refunds:</p>
            </div>
            <ul style={{ margin: 0, paddingLeft: '22px', display: 'grid', gap: '10px', fontSize: '16px', color: '#1f2933' }}>
              <li>Opened or used products.</li>
              <li>Custom-made or personalized products.</li>
              <li>Perishable items (unless damaged).</li>
              <li>Products returned after 30 days.</li>
              <li>Items without original packaging.</li>
            </ul>
          </article>

          <article style={{ display: 'grid', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>How to Request a Refund</h2>
              <p style={{ margin: '8px 0 0', fontSize: '16px', color: '#52606d' }}>Follow these steps to initiate your request:</p>
            </div>
            <ol style={{ margin: 0, paddingLeft: '22px', display: 'grid', gap: '12px', fontSize: '16px', color: '#1f2933' }}>
              <li><strong>Contact us:</strong> Reach out to our support team via phone, email, or the website.</li>
              <li><strong>Provide details:</strong> Share your order number, reason for return, and photos if applicable.</li>
              <li><strong>Get authorization:</strong> We'll send a return authorization number.</li>
              <li><strong>Package the item:</strong> Secure the item in its original packaging.</li>
              <li><strong>Ship the item:</strong> Use the provided return label to send the parcel.</li>
              <li><strong>Processing:</strong> After inspection, refunds are processed within 5–7 business days.</li>
            </ol>
          </article>

          <article style={{ display: 'grid', gap: '12px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>Refund Timelines & Methods</h2>
            <div style={{ display: 'grid', gap: '10px', fontSize: '16px', color: '#1f2933' }}>
              <ul style={{ margin: 0, paddingLeft: '22px', display: 'grid', gap: '8px' }}>
                <li><strong>Credit/Debit Cards:</strong> 5–7 business days back to the original card.</li>
                <li><strong>Net Banking / UPI:</strong> 3–5 business days to the linked account or UPI ID.</li>
                <li><strong>Cash on Delivery:</strong> 7–10 business days via bank transfer.</li>
              </ul>
              <p style={{ margin: '4px 0 0', color: '#52606d' }}>
                Refunds are always sent to the payment method used at checkout.
              </p>
            </div>
          </article>

          <article style={{ display: 'grid', gap: '12px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>Shipping & Exchanges</h2>
            <p style={{ margin: 0, fontSize: '17px', lineHeight: 1.7, color: '#52606d' }}>
              Return shipping is customer-paid unless we shipped the wrong item or a product arrived damaged; in those cases, we'll email a prepaid label. Exchanges are available for like-for-like products or any item of equal or lesser value. If you choose something pricier, simply cover the difference.
            </p>
          </article>

          <article style={{ display: 'grid', gap: '12px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>Damaged or Defective Products</h2>
            <p style={{ margin: 0, fontSize: '17px', lineHeight: 1.7, color: '#52606d' }}>
              Contact us right away with photos of the issue. We'll arrange a replacement or full refund at no additional cost.
            </p>
          </article>

          <article style={{ backgroundColor: '#f8fafc', border: '1px solid #d9e2ec', borderRadius: '16px', padding: '24px', display: 'grid', gap: '12px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '600', margin: 0, color: '#102a43' }}>Need Help?</h2>
            <p style={{ margin: 0, fontSize: '16px', color: '#52606d' }}>Our support team is happy to guide you through the process.</p>
            <div style={{ display: 'grid', gap: '6px', fontSize: '16px', color: '#1f2933' }}>
              <span><strong>Phone:</strong> +91 70931 34343</span>
              <span><strong>Email:</strong> refunds@dentalstores.in</span>
              <span><strong>Business hours:</strong> Mon–Sat, 10:30 AM – 7:00 PM IST</span>
              <span><strong>Address:</strong> Dental Stores Returns Dept., 123 Dental Avenue, New Delhi 110001</span>
            </div>
          </article>

          <article style={{ display: 'grid', gap: '10px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#102a43' }}>Important Notes</h2>
            <ul style={{ margin: 0, paddingLeft: '22px', display: 'grid', gap: '8px', fontSize: '16px', color: '#1f2933' }}>
              <li>All returns are inspected before approval.</li>
              <li>Custom or personalized items remain non-refundable.</li>
              <li>Processing may take longer during peak seasons.</li>
              <li>We reserve the right to refuse returns that don't meet policy criteria.</li>
            </ul>
          </article>
        </section>
      </div>
    </>
  );
};

export default Refunds;
